<script setup lang="ts">
import {computed, ref, watch, inject, nextTick} from "vue";
import {useFormDataStore} from "@/stores/formDataStore";
import { useCommunityDataPublic } from "@/stores/communityDataPublic";
import { useI18n } from "vue-i18n";
// Tooltip is not used
import type { FormValidation } from '@/types/form-validation';
import type { FormEnergyCommunity } from '@/stores/communityApiStore';
import { MemberType } from '../../types/src/enums';

const formDataStore = useFormDataStore();
const { t } = useI18n();

const props = defineProps<{
  communityPublic?: FormEnergyCommunity;
}>()

// const communityDataPublicStore = useCommunityDataPublic()
import { hasMemberRestrictionsSme } from '@/lib/utils/communityFlags';
const memberRestrictionsSme = computed(()=> hasMemberRestrictionsSme(props.communityPublic?.communityType));

const formValidation = inject('$formValidation') as FormValidation;

// Change default value to null (no selection)
const isCompany = computed(() => formDataStore.formData.isCompany);
// Add showContent ref to control visibility
const showContent = computed(() => formDataStore.formData.isCompany === MemberType.COMPANY || formDataStore.formData.isCompany === MemberType.PRIVATE);

// const tooltipContent = t('taxClassClarification');

const formValid = ref(false);
const theForm = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

async function formValidates() {
  if (!theForm.value) return true;
  const result = await theForm.value.validate();
  const valid = result.valid;

  if (!valid) {
    formValidation.scrollToFirstValidationError({
      nextTick
    });
  }

  return valid;
}

const itemsSalutation = [
  { title: t('mr'), value: "MR"},
  { title: t('ms'), value: "MS"},
  { title: t('none'), value: "NONE"},
];

const itemsCompanyTypeValueEpu = t('companyTypes.soleProprietorship');
const isEpu = computed(() => formDataStore.formData.legal === itemsCompanyTypeValueEpu)
const epuHasRegistrationNumber = ref<boolean | null>(null);

watch(() => [isEpu.value, epuHasRegistrationNumber.value],
  () => {
    if(isEpu.value === false) {
      // reset value
      epuHasRegistrationNumber.value = false;
      formDataStore.formData.contactData.dateOfBirth = "";
    } else { // is EPU

      if(epuHasRegistrationNumber.value) {
        formDataStore.formData.billingData.identificationType = "FBN";
        formDataStore.formData.contactData.dateOfBirth = ""; // reset other
      } else {
        formDataStore.formData.billingData.identificationType = "DateOfBirth";
        formDataStore.formData.billingData.identification = "";  // reset other
      }
    }

  })

watch(() => formDataStore.formData.legal,
  (legal: string) => {

    if (legal === t('companyTypes.association'))
      formDataStore.formData.billingData.identificationType = "ZVR"
    else if (legal === t('companyTypes.soleProprietorship')) {
      // handling is above
    } else if (legal === t('companyTypes.publicCorporation'))
      formDataStore.formData.billingData.identificationType = "other"
    else if (legal === t('companyTypes.territorialAuthority'))
      formDataStore.formData.billingData.identificationType = "other"
    else if (legal === t('companyTypes.publicOffice'))
      formDataStore.formData.billingData.identificationType = "other"
    else if (legal === t('companyTypes.other'))
      formDataStore.formData.billingData.identificationType = "other"
    else
      formDataStore.formData.billingData.identificationType = "FBN"

  })

watch(() => formDataStore.formData.isCompany,
  (newValue: string, oldValue: string) => {
    // Only clear fields when switching between "company" and "private"
    if (!oldValue || newValue === oldValue) return;

    if (newValue === MemberType.PRIVATE) {
      // Switching to private: clear company-specific fields
      formDataStore.formData.contactData.companyName = "";
      formDataStore.formData.legal = "";
      formDataStore.formData.billingData.identificationType = "";
      formDataStore.formData.billingData.identification = "";
      formDataStore.formData.confirmedSmeState = null;
      epuHasRegistrationNumber.value = null;
    } else if (newValue === MemberType.COMPANY) {
      // Switching to company: clear identification fields that are specific to private
      // (dateOfBirth can be used for EPU companies too, so we don't clear it)
      formDataStore.formData.billingData.identificationType = "";
      formDataStore.formData.billingData.identification = "";
    }
  })

const itemsCompanyType = [
  { title: t('companyTypes.association'), value: t('companyTypes.association')},
  { title: t('companyTypes.cooperative'), value: t('companyTypes.cooperative')},
  { title: t('companyTypes.soleProprietorship'), value: itemsCompanyTypeValueEpu},
  { title: t('companyTypes.gmbh'), value: t('companyTypes.gmbh')},
  { title: t('companyTypes.flexKap'), value: t('companyTypes.flexKap')},
  { title: t('companyTypes.ag'), value: t('companyTypes.ag')},
  { title: t('companyTypes.og'), value: t('companyTypes.og')},
  { title: t('companyTypes.kg'), value: t('companyTypes.kg')},
  { title: t('companyTypes.gmbhCoKg'), value: t('companyTypes.gmbhCoKg')},
  { title: t('companyTypes.publicCorporation'), value: t('companyTypes.publicCorporation')},
  { title: t('companyTypes.territorialAuthority'), value: t('companyTypes.territorialAuthority')},
  { title: t('companyTypes.publicOffice'), value: t('companyTypes.publicOffice')},
  { title: t('companyTypes.other'), value: t('companyTypes.other')},
];

const itemsIdentificationType = [
  { title: t('identificationTypes.zvrNumber'), value: "ZVR"},
  { title: t('identificationTypes.companyRegistrationNumber'), value: "FBN"},
  { title: t('identificationTypes.other'), value: "other"},
];

// Expose showContent to parent component
defineExpose({formValidates, showContent});
</script>

<template>
  <div>
    <div class="background-divider"></div>
    <div class="pa-4">
      <v-container class="masterDataContainer">
      <div class="d-flex align-center mt-4">
        <p class="text-subtitle-1 text-sm-h5">{{ t('areYouCompany') }}</p>
      </div>
      <v-form ref="theForm" v-model="formValid">

        <v-radio-group inline v-model="formDataStore.formData.isCompany">
          <v-radio :label="t('privatePerson')"
                   :value="MemberType.PRIVATE">
          </v-radio>
          <v-radio :label="t('companyAssociationEtc')"
                   :value="MemberType.COMPANY"></v-radio>
        </v-radio-group>

        <!-- Wrap content in transition, use key to trigger animation on selection change -->
        <v-fade-transition>
          <div v-if="showContent" :key="formDataStore.formData.isCompany">
        <p v-if="isCompany === MemberType.COMPANY" class="textFormHeadline">{{ t('company') }}</p>
        <v-text-field v-if="isCompany === MemberType.COMPANY" :label="t('companyNameLabel')"
                      variant="outlined" class="requiredInput"
                      v-model="formDataStore.formData.contactData.companyName"
                      :rules="[formValidation.formValidationRequired]"
        ></v-text-field>

        <v-row class="formRow" no-gutters>
          <v-col cols="12" sm="6" v-if="isCompany === MemberType.COMPANY">
            <v-select
              :label="t('legalFormLabel')"
              variant="outlined" class="requiredInput"
              :items=itemsCompanyType
              v-model="formDataStore.formData.legal"
              :rules="[formValidation.formValidationRequired]"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="6" class="formInput5" v-if="isCompany === MemberType.COMPANY && isEpu">
            <v-checkbox :label="t('companyRegistrationAvailable')"
                        v-model="epuHasRegistrationNumber"
            />
          </v-col>
          <v-col cols="12" sm="5" class="formInput5" v-if="isCompany === MemberType.COMPANY && isEpu && !epuHasRegistrationNumber">
            <v-text-field :label="t('birthDateLabel')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.contactData.dateOfBirth"
                          :rules="[formValidation.formValidationDate]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" v-if="isCompany === MemberType.COMPANY && (!isEpu)">
            <v-select
              :label="t('identificationLabel')"
              variant="outlined" class="requiredInput"
              :hint="t('identificationHint')"
              :items=itemsIdentificationType
              v-model="formDataStore.formData.billingData.identificationType"
              :rules="[formValidation.formValidationRequired]"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" v-if="isCompany === MemberType.COMPANY && (!isEpu || isEpu && epuHasRegistrationNumber)">
            <v-text-field class="requiredInput"
              :label="t('identificationLabel') + ': ' + formDataStore.formData.billingData.identificationType"
              v-model="formDataStore.formData.billingData.identification"
              :rules="[formValidation.formValidationRequired]"
              variant="outlined"
            ></v-text-field>
          </v-col>

          <v-col cols="12" v-if="isCompany === MemberType.COMPANY && memberRestrictionsSme">
            <v-checkbox :label="'* '+ t('smeConfirmation')" class="requiredInputCheckbox"
                        v-model="formDataStore.formData.confirmedSmeState"
                        :rules="[formValidation.formValidationRequiredSme]"
            />
          </v-col>
        </v-row>


        <p v-if="isCompany === MemberType.COMPANY" class="textFormHeadline">{{ t('contactPersonLabel') }}</p>
        <p v-if="isCompany === MemberType.PRIVATE" class="textFormHeadline">{{ t('personalDataLabel') }}</p>

        <v-row class="formRow" no-gutters>
          <v-col cols="12" sm="6" class="formInput6">
            <v-select
               :label="t('salutationLabel')"
               variant="outlined"
              :items="itemsSalutation"
              v-model="formDataStore.formData.contactData.salutation"
              :rules="[]"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="5" class="formInput5">
            <v-text-field :label="t('titleBefore')" variant="outlined"
                          v-model="formDataStore.formData.contactData.titleBefore"
                          :rules="[]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" class="formInput6">
            <v-text-field :label="t('firstNameLabel')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.contactData.nameFirst"
                          :rules="[formValidation.formValidationRequired]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" class="formInput6">
            <v-text-field :label="t('lastNameLabel')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.contactData.nameLast"
                          :rules="[formValidation.formValidationRequired]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="5" class="formInput5">
            <v-text-field :label="t('titleAfter')" variant="outlined"
                          v-model="formDataStore.formData.contactData.titleAfter"
                          :rules="[]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="5" class="formInput5">
            <v-text-field :label="t('phoneLabel')" variant="outlined" :hint="t('phoneNumberHint')"
                          v-model="formDataStore.formData.contactData.phoneNumber" class="requiredInput"
                          :rules="[formValidation.formValidationPhone]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="7" class="formInput7">
            <v-text-field :label="t('emailLabel')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.contactData.emailAddress"
                          :rules="[formValidation.formValidationEmail]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="5" class="formInput5">
            <v-text-field v-if="isCompany === MemberType.PRIVATE" :label="t('birthDateLabel')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.contactData.dateOfBirth"
                          :rules="[formValidation.formValidationDate]"></v-text-field>
          </v-col>
        </v-row>


        <p class="textFormHeadline">{{ t('billingAddressLabel') }}</p>
        <v-row class="formRow" no-gutters>
          <v-col cols="12" sm="7" class="formInput7">
            <v-text-field :label="t('streetLabel')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.billingData.addressPostal.address"
                          :rules="[formValidation.formValidationRequired]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="5" class="formInput5">
            <v-text-field :label="t('addressExtraLabel')" variant="outlined"
                          v-model="formDataStore.formData.billingData.addressPostal.addressExtra"
            ></v-text-field>
          </v-col>
        </v-row>


        <v-row class="formRow" no-gutters>
          <v-col cols="12" sm="4" class="formInput4">
            <v-text-field :label="t('zipLabel')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.billingData.addressPostal.zip"
                          :rules="[formValidation.formValidationZipCode]"></v-text-field>
          </v-col>
          <v-col cols="12" sm="8" class="formInput8">
            <v-text-field :label="t('cityLabel')" variant="outlined" class="requiredInput"
                          v-model="formDataStore.formData.billingData.addressPostal.city"
                          :rules="[formValidation.formValidationRequired]"></v-text-field>
          </v-col>
        </v-row>
       </div>
    </v-fade-transition>
      </v-form>
    </v-container>
    </div>
    <div class="background-divider"></div>
  </div>

</template>

<style scoped>

.masterDataContainer {
  padding: 0;

  max-width: 500px;
  margin-left: 0;
}


</style>
